/**
 * A person struck by a vehicle: whether they are touched, and how the hit is
 * shared between the two by weight (`core/vehicle/collision.ts`, `exchange`).
 *
 * A person is a round body `PERSON_RADIUS` across the hips, standing in the
 * vehicle's way; a vehicle is its footprint, moving. The hit is along the
 * line out of the vehicle's nearest face: its nose for a person walked
 * into, a side for one it swipes. It is nearly plastic, as a body against a
 * bonnet is: the person goes on at about the vehicle's speed, which is what
 * pedestrian throws are reckoned from.
 */

import type { Footprint } from '../vehicle/collision.js';
import { exchange } from '../vehicle/collision.js';

/** A person's weight, kg, and how wide they are round the hips, metres. */
export const PERSON = 75;
export const PERSON_RADIUS = 0.25;
/** Over this closing speed, m/s (10 km/h), a person struck is knocked down rather than shoved aside. */
export const KNOCKED_DOWN = 10 / 3.6;
/** How much of the closing speed a body hitting a vehicle gives back: next to none. */
const RESTITUTION = 0.05;

/** A vehicle that can strike someone: its footprint, its velocity (scene x, z, m/s) and its weight, kg. */
export interface Striker extends Footprint {
  vx: number;
  vz: number;
  mass: number;
}

/**
 * Where a round body of `radius` at (x, z) overlaps footprint `f`: the unit
 * normal out of the nearest face toward it, how deep it is in, and where it
 * would be pushed clear. Undefined when it does not touch.
 */
export function touching(f: Footprint, x: number, z: number, radius: number): { nx: number; nz: number; depth: number; x: number; z: number } | undefined {
  const rx = x - f.x, rz = z - f.z;
  // Along the heading (dx, dz), and across it to the right, (-dz, dx).
  const along = rx * f.dx + rz * f.dz, across = -rx * f.dz + rz * f.dx;
  const ha = f.halfLength + radius, hc = f.halfWidth + radius;
  if (Math.abs(along) >= ha || Math.abs(across) >= hc) return undefined;
  // Out through the nearer face.
  if (ha - Math.abs(along) < hc - Math.abs(across)) {
    const s = Math.sign(along || 1), depth = ha - Math.abs(along);
    return { nx: f.dx * s, nz: f.dz * s, depth, x: x + f.dx * s * depth, z: z + f.dz * s * depth };
  }
  const s = Math.sign(across || 1), depth = hc - Math.abs(across);
  return { nx: -f.dz * s, nz: f.dx * s, depth, x: x - f.dz * s * depth, z: z + f.dx * s * depth };
}

/** A person struck: how fast the vehicle closed on them, and each one's change in velocity (scene x, z, m/s). */
export interface Hit {
  closing: number;
  person: { x: number; z: number };
  vehicle: { x: number; z: number };
  /** The unit normal the person was pushed along, out of the vehicle. */
  nx: number;
  nz: number;
}

/**
 * Whether vehicle `s` strikes a person at (x, z) moving at (vx, vz): touching
 * them and closing on them. Shared by weight: a car hardly slows, a
 * motorbike loses a good part of its speed.
 */
export function strike(s: Striker, x: number, z: number, vx = 0, vz = 0): Hit | undefined {
  const touch = touching(s, x, z, PERSON_RADIUS);
  if (touch === undefined) return undefined;
  const closing = (s.vx - vx) * touch.nx + (s.vz - vz) * touch.nz;
  const { dv1, dv2 } = exchange(s.mass, PERSON, closing, RESTITUTION);
  if (closing <= 0) return undefined;
  return {
    closing,
    person: { x: touch.nx * dv2, z: touch.nz * dv2 },
    vehicle: { x: -touch.nx * dv1, z: -touch.nz * dv1 },
    nx: touch.nx,
    nz: touch.nz,
  };
}
